import { Box, Pagination, Typography } from "@mui/material";
import DashboardLayout from "layout";
import AppTable from "../Dashboard/Table";
import { useQuery } from "@tanstack/react-query";
import { getHotelBookings, HotelBooking } from "services/hotel";
import { LoadingState } from "components/LoadingState";
import { useState } from "react";
import TableSearch from "shared/TableSearch";

const statusColor = (status: string) => {
  if (["confirmed", "success", "successful"].includes(status)) return "green";
  if (["pending_payment", "pending"].includes(status)) return "orange";
  return "red";
};

const HotelBookings = () => {
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const limit = 20;

  const { data: bookingsResponse, isLoading } = useQuery({
    queryKey: ["hotelBookings", page],
    queryFn: () => getHotelBookings(page, limit),
  });

  const bookings = bookingsResponse?.data?.bookings || [];
  const total = bookingsResponse?.data?.total || 0;
  const totalPages = Math.max(Math.ceil(total / limit), 1);

  const headers = [
    { label: "Order ID", key: "orderId" },
    { label: "Hotel", key: "hotel" },
    { label: "Guest", key: "guest" },
    { label: "Email", key: "email" },
    // { label: "Check-in", key: "checkin" },
    // { label: "Check-out", key: "checkout" },
    { label: "Rooms", key: "rooms" },
    { label: "Amount", key: "amount" },
    { label: "Status", key: "status" },
    { label: "Date", key: "date" },
  ];

  const tableData = bookings.map((booking: HotelBooking) => ({
    orderId: booking.partner_order_id,
    hotel: booking.hotel_name,
    guest: booking.contact_info?.name || "N/A",
    email: booking.contact_info?.email || "N/A",
    // checkin: booking.checkin,
    // checkout: booking.checkout,
    rooms: String(booking.rooms),
    amount: `${booking.currency} ${Number(booking.total_price).toLocaleString()}`,
    status: (
      <Typography
        sx={{
          color: statusColor(booking.status),
          fontWeight: 600,
          fontSize: "12px",
          textTransform: "capitalize",
        }}
      >
        {booking.status.replace(/_/g, " ")}
      </Typography>
    ),
    date: new Date(booking.createdAt).toLocaleString(),
  }));

  const filteredData = searchValue.trim()
    ? tableData.filter((row) => {
        const q = searchValue.toLowerCase();
        return (
          String(row.orderId).toLowerCase().includes(q) ||
          String(row.hotel).toLowerCase().includes(q) ||
          String(row.email).toLowerCase().includes(q)
        );
      })
    : tableData;

  return (
    <DashboardLayout nextSideBar>
      {isLoading && <LoadingState />}
      <Box sx={{ width: "100%", p: { xs: 0, sm: 2 } }}>
        <Box mb={3}>
          <Typography variant="h5" fontWeight={600} color="#101828">
            Hotel Bookings
          </Typography>
          <Typography variant="body2" color="#475467">
            View and manage hotel bookings
          </Typography>
        </Box>

        <TableSearch
          noBgColor
          p="10px"
          searchPlaceHolder="Search by order ID, hotel, or email..."
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          hideFilter
          hideDate
        />

        <AppTable headers={headers} data={filteredData} />

        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default HotelBookings;
